/**
 * SEO Meta Component with JSON-LD Schema
 * Dynamically updates document title, meta tags, and structured data
 * Implements Physician, MedicalWebPage, MedicalCondition, FAQPage, and BreadcrumbList schemas
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from '../contexts/SiteContext';

// Get current site domain for canonical URLs
const getCurrentDomain = () => {
  const siteId = process.env.REACT_APP_SITE_ID || 'shoulder';
  const domains = {
    shoulder: 'https://shoulder.co.in',
    elbow: 'https://elbow.co.in',
    handandwrist: 'https://handandwrist.co.in',
    main: 'https://drdnreddyortho.com'
  };
  return domains[siteId] || domains.shoulder;
};

// SEO configurations for each page - Hyderabad-focused titles and descriptions
const SEO_CONFIG = {
  // Homepage - Primary commercial intent
  '/': {
    title: 'Shoulder Specialist in Hyderabad | Dr. Deepthi Nandan Reddy',
    h1: 'Shoulder Pain Treatment in Hyderabad',
    description: 'Shoulder surgeon at Apollo Hospitals Hyderabad. Rotator cuff, frozen shoulder, dislocation, labral tears, shoulder replacement. Non-surgical options first. Book now.',
    keywords: 'shoulder specialist Hyderabad, shoulder pain treatment Hyderabad, rotator cuff tear, frozen shoulder, shoulder surgery Hyderabad, Dr Deepthi Nandan Reddy, best shoulder doctor Hyderabad, Apollo Hospitals shoulder',
    type: 'MedicalWebPage'
  },
  '/about': {
    title: 'Dr. Deepthi Nandan Reddy | Shoulder & Elbow Surgeon Hyderabad | Apollo Hospitals',
    description: 'Dr. Deepthi Nandan Reddy - FRCS (UK) certified shoulder and elbow surgeon at Apollo Hospitals Financial District, Hyderabad. Mayo Clinic & Florida fellowship trained. 30+ years experience, 15,000+ patients treated.',
    keywords: 'Dr Deepthi Nandan Reddy, shoulder specialist Hyderabad, FRCS orthopaedic surgeon, Mayo Clinic fellowship, Apollo Hospitals Hyderabad, best shoulder doctor India',
    type: 'ProfilePage'
  },
  '/faq': {
    title: 'Shoulder Pain FAQ | Treatment Questions Answered | Dr. DN Reddy Hyderabad',
    description: 'Get answers to frequently asked questions about shoulder pain, surgery, recovery times, costs in Hyderabad, and treatment options. Expert advice from Dr. Deepthi Nandan Reddy.',
    keywords: 'shoulder pain FAQ, shoulder surgery questions Hyderabad, rotator cuff repair recovery, shoulder replacement cost India, frozen shoulder treatment',
    type: 'FAQPage'
  },
  '/testimonials': {
    title: 'Patient Reviews | Dr. Deepthi Nandan Reddy | Shoulder Surgery Hyderabad',
    description: 'Read real patient reviews about their shoulder surgery experience with Dr. Deepthi Nandan Reddy at Apollo Hospitals Hyderabad. Success stories from rotator cuff repairs, shoulder replacements.',
    keywords: 'shoulder surgery reviews Hyderabad, Dr Deepthi Nandan Reddy reviews, patient testimonials, shoulder specialist Hyderabad reviews',
    type: 'WebPage'
  },
  '/contact': {
    title: 'Book Appointment | Dr. Deepthi Nandan Reddy | Apollo Hospitals Hyderabad',
    description: 'Book an appointment with Dr. Deepthi Nandan Reddy at Apollo Hospitals Financial District, Hyderabad. Call +91 99599 64567 or book online. Consultation hours 9 AM - 5 PM.',
    keywords: 'book shoulder specialist appointment Hyderabad, Dr Deepthi Nandan Reddy appointment, Apollo Hospitals Financial District, shoulder doctor Hyderabad',
    type: 'ContactPage'
  },
  '/blog': {
    title: 'Shoulder Health Blog | Expert Insights | Dr. DN Reddy Hyderabad',
    description: 'Educational articles on shoulder health, treatment options, rehabilitation exercises, and prevention tips from Dr. Deepthi Nandan Reddy, shoulder specialist in Hyderabad.',
    keywords: 'shoulder health blog, shoulder exercises, rotator cuff exercises, shoulder injury prevention, orthopedic health tips Hyderabad',
    type: 'Blog'
  },
  
  // Condition pages - Hyderabad-focused for local SEO
  '/frozen-shoulder': {
    title: 'Frozen Shoulder Treatment in Hyderabad – Stages, Exercises, Injections, Surgery | Dr. DN Reddy',
    description: 'Frozen shoulder (adhesive capsulitis) treatment in Hyderabad. AAOS guidelines-based care. Stages, physiotherapy, steroid injections, hydrodilatation, arthroscopic capsular release. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'frozen shoulder treatment Hyderabad, adhesive capsulitis Hyderabad, frozen shoulder surgery, capsular release, shoulder stiffness treatment, frozen shoulder stages',
    type: 'MedicalCondition',
    medicalCode: 'M75.0'
  },
  '/rotator-cuff-tear': {
    title: 'Rotator Cuff Tear Treatment in Hyderabad – Arthroscopic Repair & Recovery | Dr. DN Reddy',
    description: 'Rotator cuff tear treatment in Hyderabad. Arthroscopic repair with 85-95% success rate. Partial vs full-thickness tears, PRP therapy, surgical options, recovery timeline. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'rotator cuff tear Hyderabad, rotator cuff repair, arthroscopic shoulder surgery Hyderabad, rotator cuff surgery cost, shoulder tendon tear, rotator cuff treatment',
    type: 'MedicalCondition',
    medicalCode: 'M75.1'
  },
  '/shoulder-dislocation': {
    title: 'Shoulder Dislocation Treatment in Hyderabad – Bankart Repair & Instability | Dr. DN Reddy',
    description: 'Shoulder dislocation and instability treatment in Hyderabad. First-time vs recurrent dislocation, conservative management, Bankart repair, Latarjet procedure. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'shoulder dislocation treatment Hyderabad, shoulder instability, Bankart repair, recurrent shoulder dislocation, shoulder subluxation, Latarjet surgery',
    type: 'MedicalCondition',
    medicalCode: 'S43.0'
  },
  '/shoulder-arthritis': {
    title: 'Shoulder Arthritis Treatment in Hyderabad – Joint Replacement Options | Dr. DN Reddy',
    description: 'Shoulder arthritis treatment in Hyderabad including total and reverse shoulder replacement. 95% success rate at 10 years. Osteoarthritis, rheumatoid arthritis, post-traumatic arthritis. Dr. Deepthi Nandan Reddy.',
    keywords: 'shoulder arthritis treatment Hyderabad, shoulder replacement Hyderabad, reverse shoulder replacement, total shoulder arthroplasty, osteoarthritis shoulder',
    type: 'MedicalCondition',
    medicalCode: 'M19.01'
  },
  '/shoulder-impingement': {
    title: 'Shoulder Impingement Treatment in Hyderabad – Diagnosis & Arthroscopic Surgery | Dr. DN Reddy',
    description: 'Shoulder impingement syndrome treatment in Hyderabad. Neer classification, subacromial decompression, physiotherapy, cortisone injections. 85-90% success rate. Dr. Deepthi Nandan Reddy at Apollo.',
    keywords: 'shoulder impingement treatment Hyderabad, subacromial impingement, rotator cuff impingement, shoulder pain overhead, arthroscopic decompression',
    type: 'MedicalCondition',
    medicalCode: 'M75.4'
  },
  '/shoulder-replacement': {
    title: 'Shoulder Replacement Surgery in Hyderabad – Cost, Recovery, Reverse vs Total | Dr. DN Reddy',
    description: 'Shoulder replacement surgery in Hyderabad. Total vs reverse shoulder arthroplasty, cost information, 95% implant survival at 10 years, recovery timeline. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'shoulder replacement surgery Hyderabad, total shoulder replacement cost, reverse shoulder replacement India, shoulder arthroplasty, joint replacement Hyderabad',
    type: 'MedicalProcedure'
  },
  '/biceps-tendon-injury': {
    title: 'Biceps Tendon Tear at the Shoulder (Popeye Sign) – Treatment in Hyderabad | Dr. DN Reddy',
    description: 'Biceps tendon tear treatment in Hyderabad. Proximal vs distal tears, Popeye deformity, biceps tenodesis, SLAP repair options. Conservative and surgical treatment. Dr. Deepthi Nandan Reddy.',
    keywords: 'biceps tendon tear Hyderabad, biceps tenodesis, biceps rupture treatment, long head biceps tear, Popeye deformity, biceps injury shoulder',
    type: 'MedicalCondition',
    medicalCode: 'S46.1'
  },
  '/shoulder-fracture': {
    title: 'Shoulder Fracture Treatment in Hyderabad – Proximal Humerus & Surgery | Dr. DN Reddy',
    description: 'Shoulder fracture (proximal humerus) treatment in Hyderabad. Neer classification, non-surgical vs surgical options, plate fixation, hemiarthroplasty. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'shoulder fracture treatment Hyderabad, proximal humerus fracture, broken shoulder, shoulder fracture surgery, humerus fracture',
    type: 'MedicalCondition',
    medicalCode: 'S42.2'
  },
  '/shoulder-pain': {
    title: 'Shoulder Pain Treatment in Hyderabad – Causes, Diagnosis & Expert Care | Dr. DN Reddy',
    description: 'Comprehensive shoulder pain diagnosis and treatment in Hyderabad. Night pain, rotator cuff problems, arthritis, referred pain. Red flags, when to see a specialist. Dr. Deepthi Nandan Reddy.',
    keywords: 'shoulder pain treatment Hyderabad, shoulder pain at night, shoulder pain causes, best shoulder doctor Hyderabad, chronic shoulder pain, shoulder specialist',
    type: 'MedicalWebPage'
  },
  '/arthroscopic-shoulder-surgery': {
    title: 'Arthroscopic Shoulder Surgery in Hyderabad – Keyhole Procedures | Dr. DN Reddy',
    description: 'Minimally invasive arthroscopic shoulder surgery in Hyderabad. Rotator cuff repair, labral repair, impingement surgery, loose body removal. Less pain, faster recovery. Dr. Deepthi Nandan Reddy.',
    keywords: 'arthroscopic shoulder surgery Hyderabad, keyhole shoulder surgery, minimally invasive shoulder surgery, shoulder arthroscopy cost, shoulder scope',
    type: 'MedicalProcedure'
  },
  '/calcific-tendonitis': {
    title: 'Calcific Tendonitis Treatment in Hyderabad – Barbotage, Shockwave & Surgery | Dr. DN Reddy',
    description: 'Calcific tendonitis (calcium deposits in rotator cuff) treatment in Hyderabad. Barbotage procedure, extracorporeal shockwave therapy (ESWT), arthroscopic removal. Dr. Deepthi Nandan Reddy.',
    keywords: 'calcific tendonitis treatment Hyderabad, calcium deposits shoulder, barbotage procedure, shockwave therapy shoulder, calcific tendinitis',
    type: 'MedicalCondition',
    medicalCode: 'M75.3'
  },
  '/slap-tears': {
    title: 'SLAP Tear Treatment in Hyderabad – Diagnosis, Repair & Recovery | Dr. DN Reddy',
    description: 'SLAP tear (superior labral tear) treatment in Hyderabad. Snyder classification, Type II SLAP repair vs biceps tenodesis, throwing athlete injuries. Dr. Deepthi Nandan Reddy at Apollo.',
    keywords: 'SLAP tear treatment Hyderabad, superior labral tear, labrum tear shoulder, SLAP repair surgery, throwing shoulder injury',
    type: 'MedicalCondition',
    medicalCode: 'S43.4'
  },
  '/clavicle-fracture': {
    title: 'Clavicle Fracture Treatment in Hyderabad – Surgery & Recovery | Dr. DN Reddy',
    description: 'Clavicle (collarbone) fracture treatment in Hyderabad. Midshaft vs distal clavicle fractures, plate fixation, non-operative management, COTS trial evidence. Dr. Deepthi Nandan Reddy.',
    keywords: 'clavicle fracture treatment Hyderabad, broken collarbone, clavicle surgery, collarbone fracture healing, clavicle plate',
    type: 'MedicalCondition',
    medicalCode: 'S42.0'
  },
  '/scapula-fracture': {
    title: 'Scapula (Shoulder Blade) Fracture – Treatment & Recovery in Hyderabad | Dr. DN Reddy',
    description: 'Scapula fracture treatment in Hyderabad. High-energy trauma management, glenoid fractures, surgical indications, floating shoulder. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'scapula fracture treatment Hyderabad, shoulder blade fracture, glenoid fracture, floating shoulder, scapula surgery',
    type: 'MedicalCondition',
    medicalCode: 'S42.1'
  },
  '/ac-joint-injury': {
    title: 'AC Joint Injury Treatment in Hyderabad – Separation & Reconstruction | Dr. DN Reddy',
    description: 'AC joint injury (shoulder separation) treatment in Hyderabad. Rockwood Grade I-VI classification, conservative vs surgical management, coracoclavicular reconstruction. Dr. Deepthi Nandan Reddy.',
    keywords: 'AC joint injury Hyderabad, shoulder separation, acromioclavicular joint, AC joint surgery, Rockwood classification',
    type: 'MedicalCondition',
    medicalCode: 'S43.1'
  },
  '/sc-joint-injury': {
    title: 'SC Joint Injury Treatment in Hyderabad – Sternoclavicular Dislocation | Dr. DN Reddy',
    description: 'SC joint (sternoclavicular) injury treatment in Hyderabad. Anterior vs posterior dislocation, emergency protocols for posterior injuries, surgical reduction. Dr. Deepthi Nandan Reddy.',
    keywords: 'SC joint injury Hyderabad, sternoclavicular dislocation, SC joint surgery, chest bone shoulder pain',
    type: 'MedicalCondition',
    medicalCode: 'S43.2'
  },
  '/labral-tears': {
    title: 'Shoulder Labral Tear Treatment in Hyderabad – SLAP & Bankart Repair | Dr. DN Reddy',
    description: 'Labral tear treatment in Hyderabad. Bankart lesions, SLAP tears, posterior labral tears, arthroscopic repair techniques, rehabilitation. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'labral tear treatment Hyderabad, Bankart tear, shoulder labrum surgery, labral repair arthroscopy, glenoid labrum tear',
    type: 'MedicalCondition',
    medicalCode: 'S43.4'
  },
  '/mdi': {
    title: 'Multidirectional Shoulder Instability (MDI) – Diagnosis & Treatment in Hyderabad | Dr. DN Reddy',
    description: 'MDI (multidirectional instability) treatment in Hyderabad. AMBRII mnemonic, rehabilitation-first approach, capsular shift surgery. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'MDI treatment Hyderabad, multidirectional instability shoulder, loose shoulder, capsular shift surgery, shoulder hyperlaxity',
    type: 'MedicalCondition',
    medicalCode: 'M24.3'
  },
  '/reverse-shoulder-replacement': {
    title: 'Reverse Shoulder Replacement in Hyderabad – Indications & Outcomes | Dr. DN Reddy',
    description: 'Reverse shoulder replacement surgery in Hyderabad. Grammont design, rotator cuff arthropathy, massive irreparable cuff tears, proximal humerus fractures. Dr. Deepthi Nandan Reddy.',
    keywords: 'reverse shoulder replacement Hyderabad, reverse total shoulder arthroplasty, rotator cuff arthropathy, shoulder replacement cuff tear',
    type: 'MedicalProcedure'
  },
  // New SEO Hub Pages
  '/shoulder-pain-treatment-hyderabad': {
    title: 'Shoulder Pain Treatment in Hyderabad – Best Shoulder Specialist | Dr. DN Reddy',
    description: 'Expert shoulder pain treatment in Hyderabad by Dr. Deepthi Nandan Reddy at Apollo Hospitals. Rotator cuff, frozen shoulder, arthritis, instability. UK & Mayo Clinic trained. Non-surgical options first.',
    keywords: 'shoulder pain treatment Hyderabad, best shoulder doctor Hyderabad, shoulder specialist Hyderabad, shoulder pain specialist, shoulder doctor near me, Apollo Hospitals shoulder',
    type: 'MedicalWebPage'
  },
  '/shoulder-anatomy': {
    title: 'Shoulder Anatomy Guide – Bones, Joints, Muscles & Structures | Shoulder.co.in',
    description: 'Complete guide to shoulder anatomy: scapula (shoulder blade), clavicle (collarbone), humerus, rotator cuff muscles, labrum, and joints. Educational resource by Dr. Deepthi Nandan Reddy.',
    keywords: 'shoulder anatomy, scapula, shoulder blade, clavicle, collarbone, arm bones names, rotator cuff anatomy, labrum, glenohumeral joint',
    type: 'WebPage'
  },
  '/shoulder-pain-at-night': {
    title: 'Shoulder Pain at Night – Causes & Treatment in Hyderabad | Dr. DN Reddy',
    description: 'Why does your shoulder hurt at night? Common causes (rotator cuff, frozen shoulder, impingement), best sleeping positions, when to see a doctor. Treatment by Dr. Deepthi Nandan Reddy at Apollo Hospitals Hyderabad.',
    keywords: 'shoulder pain at night, shoulder hurts when sleeping, night shoulder pain, shoulder pain lying down, shoulder pain sleep, why does my shoulder hurt at night, shoulder specialist Hyderabad',
    type: 'MedicalWebPage'
  },
  '/non-surgical-shoulder-treatment-hyderabad': {
    title: 'Non-Surgical Shoulder Treatment in Hyderabad – Physio, Injections, PRP | Dr. DN Reddy',
    description: 'Non-surgical shoulder treatment options in Hyderabad: physiotherapy, corticosteroid injections, PRP therapy, shockwave, hydrodilatation. 70% of shoulder conditions improve without surgery. Dr. Deepthi Nandan Reddy at Apollo Hospitals.',
    keywords: 'non surgical shoulder treatment Hyderabad, shoulder pain treatment without surgery, PRP injection shoulder, physiotherapy shoulder, corticosteroid injection shoulder, shockwave therapy shoulder, conservative shoulder treatment',
    type: 'MedicalWebPage'
  }
};

// Generate Physician JSON-LD Schema
const generatePhysicianSchema = (doctor, currentDomain) => {
  // Build sameAs array excluding the current site
  const sameAsLinks = [
    doctor.externalProfiles.mainWebsite,
    doctor.externalProfiles.apolloProfile,
    doctor.externalProfiles.practo,
    doctor.externalProfiles.justdial,
    doctor.externalProfiles.instagram,
    doctor.externalProfiles.shoulderSite,
    doctor.externalProfiles.elbowSite,
    doctor.externalProfiles.handWristSite
  ].filter(url => !url.includes(currentDomain.replace('https://', '')));

  return {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    '@id': `${currentDomain}/#physician`,
    name: doctor.name,
    alternateName: doctor.alternateNames,
    image: doctor.doctorImage,
    description: `${doctor.title} at ${doctor.hospitalFull}. ${doctor.fellowships}. Specialized in ${doctor.specialization}.`,
    medicalSpecialty: doctor.medicalSpecialty,
    telephone: doctor.phone,
    email: doctor.email,
    url: currentDomain,
    hospitalAffiliation: {
      '@type': 'Hospital',
      name: doctor.hospitalFull,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'D.No-2-105/6/SE, Financial District, Nanakramguda',
        addressLocality: 'Hyderabad',
        addressRegion: 'Telangana',
        postalCode: doctor.postalCode,
        addressCountry: 'IN'
      }
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'D.No-2-105/6/SE, Financial District, Nanakramguda',
      addressLocality: 'Hyderabad',
      addressRegion: 'Telangana',
      postalCode: doctor.postalCode,
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 17.4326,
      longitude: 78.3675
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '17:00'
    },
    sameAs: sameAsLinks,
    knowsAbout: [
      'Shoulder Surgery',
      'Rotator Cuff Repair',
      'Shoulder Replacement',
      'Arthroscopic Surgery',
      'Frozen Shoulder Treatment',
      'Sports Medicine'
    ],
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'degree',
        name: 'MBBS'
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'degree',
        name: 'MS (Orthopaedics)'
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'degree',
        name: 'FRCS (Ortho, UK)'
      }
    ],
    alumniOf: [
      {
        '@type': 'EducationalOrganization',
        name: 'Mayo Clinic'
      },
      {
        '@type': 'EducationalOrganization',
        name: 'Florida Orthopaedic Institute'
      }
    ]
  };
};

// Generate MedicalWebPage schema
const generateMedicalWebPageSchema = (pageConfig, doctor, currentDomain, path) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${currentDomain}${path}`,
    name: pageConfig.title,
    description: pageConfig.description,
    url: `${currentDomain}${path}`,
    mainEntity: {
      '@id': `${currentDomain}/#physician`
    },
    about: {
      '@type': 'MedicalCondition',
      name: pageConfig.title.split(' – ')[0].replace(' Treatment in Hyderabad', ''),
      possibleTreatment: {
        '@type': 'MedicalTherapy',
        name: 'Expert orthopedic treatment'
      }
    },
    author: {
      '@type': 'Physician',
      '@id': `${currentDomain}/#physician`,
      name: doctor.name
    },
    publisher: {
      '@type': 'Organization',
      name: doctor.name,
      logo: {
        '@type': 'ImageObject',
        url: doctor.doctorImage
      }
    },
    dateModified: new Date().toISOString().split('T')[0],
    inLanguage: 'en-IN',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Shoulder.co.in',
      url: currentDomain
    }
  };
};

// Generate BreadcrumbList schema
const generateBreadcrumbSchema = (path, pageTitle, currentDomain) => {
  const breadcrumbs = [
    { name: 'Home', url: currentDomain }
  ];
  
  if (path !== '/') {
    breadcrumbs.push({
      name: pageTitle.split(' | ')[0].split(' – ')[0],
      url: `${currentDomain}${path}`
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

// Generate LocalBusiness schema for NAP consistency
const generateLocalBusinessSchema = (doctor, currentDomain) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    '@id': `${currentDomain}/#business`,
    name: `${doctor.name} - Shoulder Clinic`,
    image: doctor.doctorImage,
    telephone: doctor.phone,
    email: doctor.email,
    url: currentDomain,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'D.No-2-105/6/SE, Financial District, Nanakramguda',
      addressLocality: 'Hyderabad',
      addressRegion: 'Telangana',
      postalCode: doctor.postalCode,
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 17.4326,
      longitude: 78.3675
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '17:00'
    },
    priceRange: '₹₹',
    areaServed: {
      '@type': 'City',
      name: 'Hyderabad'
    }
  };
};

export const SEOHead = () => {
  const location = useLocation();
  const { doctor } = useSite();
  const currentDomain = getCurrentDomain();
  
  useEffect(() => {
    const path = location.pathname;
    const seoConfig = SEO_CONFIG[path] || SEO_CONFIG['/'];
    
    // Update document title
    document.title = seoConfig.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seoConfig.description);
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', seoConfig.keywords);
    
    // Update Open Graph tags
    const ogTags = {
      'og:title': seoConfig.title,
      'og:description': seoConfig.description,
      'og:url': `${currentDomain}${path === '/' ? '' : path}`,
      'og:type': 'website',
      'og:site_name': 'Shoulder.co.in',
      'og:locale': 'en_IN'
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    
    // Update Twitter tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': seoConfig.title,
      'twitter:description': seoConfig.description
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    
    // Update canonical URL (no trailing slash)
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${currentDomain}${path === '/' ? '' : path}`);
    
    // Remove existing JSON-LD scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    
    // Add Physician JSON-LD (on homepage and about page)
    if (path === '/' || path === '/about') {
      const physicianScript = document.createElement('script');
      physicianScript.type = 'application/ld+json';
      physicianScript.textContent = JSON.stringify(generatePhysicianSchema(doctor, currentDomain));
      document.head.appendChild(physicianScript);
    }
    
    // Add MedicalWebPage schema for condition pages
    if (seoConfig.type === 'MedicalCondition' || seoConfig.type === 'MedicalWebPage' || seoConfig.type === 'MedicalProcedure') {
      const medicalScript = document.createElement('script');
      medicalScript.type = 'application/ld+json';
      medicalScript.textContent = JSON.stringify(generateMedicalWebPageSchema(seoConfig, doctor, currentDomain, path));
      document.head.appendChild(medicalScript);
    }
    
    // Add Breadcrumb schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(generateBreadcrumbSchema(path, seoConfig.title, currentDomain));
    document.head.appendChild(breadcrumbScript);
    
    // Add LocalBusiness schema on homepage
    if (path === '/') {
      const businessScript = document.createElement('script');
      businessScript.type = 'application/ld+json';
      businessScript.textContent = JSON.stringify(generateLocalBusinessSchema(doctor, currentDomain));
      document.head.appendChild(businessScript);
    }
    
  }, [location.pathname, doctor]);
  
  return null;
};

export default SEOHead;
